import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { useSession } from '@/lib/auth-client';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { ModeToggle } from '@/components/mode-toggle';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  RiSettings3Line,
  RiUserLine,
  RiBellLine,
  RiPaletteLine,
  RiCodeLine,
  RiImageEditLine,
  RiLoader2Line,
  RiGuideLine,
  RiRefreshLine,
} from '@remixicon/react';
import { useOnboardingStore } from '@/stores/onboarding-store';

export function SettingsPage() {
  const { data: session } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { resetOnboarding, openOnboarding } = useOnboardingStore();
  const [settings, setSettings] = useState({
    name: session?.user?.name || '',
    email: session?.user?.email || '',
    image: session?.user?.image || '',
    notifications: {
      email: true,
      queryComplete: true,
      queryError: true,
      weeklyReport: false,
    },
    preferences: {
      theme: 'system',
      editorFontSize: '14',
      autoSave: true,
      confirmDelete: true,
    },
  });

  useEffect(() => {
    if (session?.user?.id) {
      loadPreferences();
    }
  }, [session?.user?.id]);

  const loadPreferences = async () => {
    try {
      const data = await api.getPreferences(session!.user!.id);
      setSettings(prev => ({
        ...prev,
        notifications: data.preferences.notifications,
        preferences: data.preferences.editor,
      }));
    } catch (error) {
      console.error('Failed to load preferences', error);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !session?.user?.id) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    try {
      setIsUploadingAvatar(true);

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;

        await api.updateProfile(session.user.id, {
          name: settings.name,
          image: base64String
        });

        setSettings(prev => ({ ...prev, image: base64String }));
        toast.success('Avatar updated successfully');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to upload avatar', error);
      toast.error('Failed to upload avatar');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    if (!session?.user?.id) return;

    try {
      setIsLoading(true);

      await api.updateProfile(session.user.id, {
        name: settings.name,
        image: settings.image
      });

      await api.updatePreferences(session.user.id, {
        notifications: settings.notifications,
        editor: settings.preferences,
        theme: settings.preferences.theme,
      });

      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings', error);
      toast.error('Failed to save settings');
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b backdrop-blur-sm bg-background/80">
          <div className="flex items-center gap-2 px-3">
            <SidebarTrigger />
            <Separator orientation="vertical" className="h-6" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Settings</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="ml-auto flex items-center gap-2 px-3">
            <ModeToggle />
          </div>
        </header>

        <div className="flex-1 overflow-auto p-4 md:p-6">
          <div className="max-w-5xl mx-auto space-y-6">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-6"
            >
              <div className="relative group">
                <Avatar className="w-20 h-20 ring-4 ring-background shadow-lg">
                  <AvatarImage src={settings.image} alt={settings.name} />
                  <AvatarFallback className="text-2xl font-semibold bg-primary/10 text-primary">
                    {getInitials(settings.name || 'U')}
                  </AvatarFallback>
                </Avatar>
                <Button
                  size="sm"
                  variant="secondary"
                  className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full p-0 shadow-lg hover:scale-110 transition-transform"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingAvatar}
                >
                  {isUploadingAvatar ? (
                    <RiLoader2Line className="w-4 h-4 animate-spin" />
                  ) : (
                    <RiImageEditLine className="w-4 h-4" />
                  )}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl font-bold mb-1">Settings</h1>
                <p className="text-muted-foreground text-sm">
                  Manage your account preferences and application settings
                </p>
              </div>
            </motion.div>

            {/* Tabs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Tabs defaultValue="profile" className="w-full">
                <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
                  <TabsTrigger value="profile" className="gap-2">
                    <RiUserLine className="w-4 h-4" />
                    <span className="hidden sm:inline">Profile</span>
                  </TabsTrigger>
                  <TabsTrigger value="notifications" className="gap-2">
                    <RiBellLine className="w-4 h-4" />
                    <span className="hidden sm:inline">Notifications</span>
                  </TabsTrigger>
                  <TabsTrigger value="editor" className="gap-2">
                    <RiCodeLine className="w-4 h-4" />
                    <span className="hidden sm:inline">Editor</span>
                  </TabsTrigger>
                  <TabsTrigger value="tutorial" className="gap-2">
                    <RiGuideLine className="w-4 h-4" />
                    <span className="hidden sm:inline">Tutorial</span>
                  </TabsTrigger>
                </TabsList>

                {/* Profile Tab */}
                <TabsContent value="profile" className="mt-6 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Profile Information</CardTitle>
                      <CardDescription>
                        Update your personal information and profile details
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name</Label>
                          <Input
                            id="name"
                            placeholder="Enter your full name"
                            value={settings.name}
                            onChange={(e) =>
                              setSettings({ ...settings, name: e.target.value })
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address</Label>
                          <Input
                            id="email"
                            type="email"
                            value={settings.email}
                            disabled
                            className="bg-muted/50"
                          />
                          <p className="text-xs text-muted-foreground">
                            Email cannot be changed. Contact support if needed.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Notifications Tab */}
                <TabsContent value="notifications" className="mt-6 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Notification Preferences</CardTitle>
                      <CardDescription>
                        Configure how and when you receive notifications
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="space-y-0.5">
                          <Label className="font-medium">Email Notifications</Label>
                          <p className="text-sm text-muted-foreground">
                            Receive notifications via email
                          </p>
                        </div>
                        <Switch
                          checked={settings.notifications.email}
                          onCheckedChange={(checked) =>
                            setSettings({
                              ...settings,
                              notifications: { ...settings.notifications, email: checked },
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="space-y-0.5">
                          <Label className="font-medium">Query Completion</Label>
                          <p className="text-sm text-muted-foreground">
                            Notify when queries finish executing
                          </p>
                        </div>
                        <Switch
                          checked={settings.notifications.queryComplete}
                          onCheckedChange={(checked) =>
                            setSettings({
                              ...settings,
                              notifications: { ...settings.notifications, queryComplete: checked },
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="space-y-0.5">
                          <Label className="font-medium">Query Errors</Label>
                          <p className="text-sm text-muted-foreground">
                            Notify when queries fail
                          </p>
                        </div>
                        <Switch
                          checked={settings.notifications.queryError}
                          onCheckedChange={(checked) =>
                            setSettings({
                              ...settings,
                              notifications: { ...settings.notifications, queryError: checked },
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="space-y-0.5">
                          <Label className="font-medium">Weekly Reports</Label>
                          <p className="text-sm text-muted-foreground">
                            Receive weekly usage summaries
                          </p>
                        </div>
                        <Switch
                          checked={settings.notifications.weeklyReport}
                          onCheckedChange={(checked) =>
                            setSettings({
                              ...settings,
                              notifications: { ...settings.notifications, weeklyReport: checked },
                            })
                          }
                        />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Editor Tab */}
                <TabsContent value="editor" className="mt-6 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Editor Preferences</CardTitle>
                      <CardDescription>
                        Customize your query editor experience
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="fontSize">Font Size</Label>
                        <Select
                          value={settings.preferences.editorFontSize}
                          onValueChange={(value) =>
                            setSettings({
                              ...settings,
                              preferences: { ...settings.preferences, editorFontSize: value },
                            })
                          }
                        >
                          <SelectTrigger id="fontSize">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="12">12px - Small</SelectItem>
                            <SelectItem value="14">14px - Default</SelectItem>
                            <SelectItem value="16">16px - Large</SelectItem>
                            <SelectItem value="18">18px - Extra Large</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Separator />
                      <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="space-y-0.5">
                          <Label className="font-medium">Auto-save Queries</Label>
                          <p className="text-sm text-muted-foreground">
                            Automatically save query drafts
                          </p>
                        </div>
                        <Switch
                          checked={settings.preferences.autoSave}
                          onCheckedChange={(checked) =>
                            setSettings({
                              ...settings,
                              preferences: { ...settings.preferences, autoSave: checked },
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="space-y-0.5">
                          <Label className="font-medium">Confirm Deletions</Label>
                          <p className="text-sm text-muted-foreground">
                            Show confirmation dialog before deleting
                          </p>
                        </div>
                        <Switch
                          checked={settings.preferences.confirmDelete}
                          onCheckedChange={(checked) =>
                            setSettings({
                              ...settings,
                              preferences: { ...settings.preferences, confirmDelete: checked },
                            })
                          }
                        />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Tutorial Tab */}
                <TabsContent value="tutorial" className="mt-6 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Onboarding Tutorial</CardTitle>
                      <CardDescription>
                        Take the getting started tour to learn about Moniva's key features
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="p-4 rounded-lg border bg-muted/50">
                        <p className="text-sm text-muted-foreground mb-4">
                          Restart the onboarding tutorial to review Moniva's features and learn how to make the most of the platform.
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              resetOnboarding();
                              toast.success('Onboarding tutorial has been reset');
                            }}
                            className="gap-2"
                          >
                            <RiRefreshLine className="w-4 h-4" />
                            Reset Tutorial
                          </Button>
                          <Button
                            onClick={() => {
                              openOnboarding();
                            }}
                            className="gap-2"
                          >
                            <RiGuideLine className="w-4 h-4" />
                            Start Tutorial
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </motion.div>

            {/* Save Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex items-center justify-between p-4 rounded-lg border bg-card"
            >
              <div>
                <p className="font-medium">Save your changes</p>
                <p className="text-sm text-muted-foreground">
                  All preferences will be applied immediately
                </p>
              </div>
              <Button
                onClick={handleSave}
                size="lg"
                disabled={isLoading}
                className="min-w-[140px]"
              >
                {isLoading ? (
                  <>
                    <RiLoader2Line className="w-5 h-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </motion.div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
